import { useEffect, useState } from 'react'
import {
  corDoTextoSobre,
  type CampoDto,
  type ContatoDto,
  type FunilDto,
  type NegociacaoDto,
  type TagDto,
  type MembroDaEquipeDto,
} from '@crm/shared'
import { ErroRequisicao } from '../../api/cliente'
import {
  useAtualizarContato,
  useAtualizarNegociacao,
  useContato,
  useCriarContato,
  useExcluirContato,
  useMovimentos,
} from '../../api/hooks-leads'
import { useEu } from '../../api/hooks'
import {
  CampoCustomizado,
  montarCampos,
  valoresParaFormulario,
} from '../../componentes/campos-customizados'
import { BotaoNovaEtiqueta } from '../../componentes/etiqueta-nova'
import { SeletorDeEmpresa } from '../../componentes/SeletorDeEmpresa'
import { usePerguntar } from '../../componentes/perguntar'
import { Aviso, Botao, Campo, Carregando, Modal, juntar } from '../../componentes/ui'
import { pode } from '../../permissao'
import { formatarDataHora, formatarMoeda, lerMoeda, plural } from '../../formato'
import { HistoricoDeNegociacoes } from './HistoricoDeNegociacoes'

interface Props {
  aberto: boolean
  /** Contato já carregado. Vindo do quadro pode ser nulo — aí busca por `contatoId`. */
  contato: ContatoDto | null
  contatoId?: string | null
  /** O ciclo aberto pelo card. Nulo quando se abre o cliente, e não o negócio. */
  negociacao?: NegociacaoDto | null
  campos: CampoDto[]
  tags: TagDto[]
  usuarios: MembroDaEquipeDto[]
  aoFechar: () => void
  /** Abre outro ciclo para o mesmo cliente (4.4). */
  aoIniciarNovaNegociacao?: (contatoId: string, nome: string) => void
  /**
   * Mostra o seletor de funil no cadastro.
   *
   * Contato novo pode nascer com a primeira negociação já aberta — é como o
   * "Novo lead" funciona na prática, e exigir um segundo passo faria o card
   * não aparecer no quadro logo depois de criar.
   */
  funis?: FunilDto[]
  funilPadrao?: string | null
  /** Sobrepõe o título. O bate-papo chama isto de "adicionar ao CRM". */
  titulo?: string
}

type Aba = 'dados' | 'historico'

export function ModalContato({
  aberto,
  contato,
  contatoId,
  negociacao,
  campos,
  tags,
  usuarios,
  aoFechar,
  aoIniciarNovaNegociacao,
  funis,
  funilPadrao,
  titulo,
}: Props) {
  const criar = useCriarContato()
  const atualizar = useAtualizarContato()
  const atualizarNegociacao = useAtualizarNegociacao()
  const excluir = useExcluirContato()
  const { pedirConfirmacao, dialogo } = usePerguntar()

  /*
    A ficha vem sempre do servidor, mesmo quando a lista já entregou o
    contato: ligar a uma empresa muda o contato na hora, sem passar pelo
    Salvar, e a cópia que a lista entregou continuaria mostrando a empresa
    antiga. Enquanto a consulta não volta, a cópia da lista segura a tela.
  */
  const idDoContato = contato?.id ?? contatoId ?? null
  const { data: buscado } = useContato(idDoContato)
  const ficha = buscado ?? contato ?? null
  const { data: eu } = useEu()

  const [aba, setAba] = useState<Aba>('dados')
  const [carregadoDe, setCarregadoDe] = useState<string | null>(null)
  const [nome, setNome] = useState('')
  const [telefone, setTelefone] = useState('')
  const [documento, setDocumento] = useState('')
  const [vendedorId, setVendedorId] = useState('')
  const [tagIds, setTagIds] = useState<string[]>([])
  const [valoresDeCampo, setValoresDeCampo] = useState<Record<string, string>>({})
  const [tituloNegocio, setTituloNegocio] = useState('')
  const [valorNegocio, setValorNegocio] = useState('')
  const [funilEscolhido, setFunilEscolhido] = useState('')

  const escolheFunil = Array.isArray(funis) && funis.length > 0
  const funisAtivos = (funis ?? []).filter((f) => f.ativo)
  const funilAtual = funisAtivos.find((f) => f.id === funilEscolhido) ?? funisAtivos[0] ?? null

  /*
    Aberta pelo id (o quadro de Leads e as etapas no alto da conversa), a
    ficha chega depois da janela. Carregar só uma vez enchia o formulário com o
    vazio e não carregava de novo quando ela chegava, porque a chave da
    negociação já era a mesma: Nome e Telefone ficavam em branco. Agora a
    espera tem chave própria: o formulário abre limpo (e não com o contato
    da vez anterior), e carrega de novo quando a ficha chega.
  */
  const esperandoFicha = Boolean(idDoContato) && !ficha
  const chaveAtual = esperandoFicha
    ? `esperando:${idDoContato}`
    : (negociacao?.id ?? ficha?.id ?? (aberto ? 'novo' : null))

  // Fechou: esquece o que carregou. Sem isto, abrir "novo" duas vezes seguidas
  // (ou reabrir a mesma ficha depois de Cancelar) trazia o que estava na tela antes.
  if (!aberto && carregadoDe !== null) setCarregadoDe(null)

  if (aberto && carregadoDe !== chaveAtual) {
    setCarregadoDe(chaveAtual)
    setAba('dados')
    setNome(ficha?.nome ?? '')
    // Telefone mascarado não volta para o formulário: reenviar a máscara
    // gravaria "31*****-6935" por cima do número real.
    setTelefone(ficha && !ficha.telefoneMascarado ? (ficha.telefonePrincipal ?? '') : '')
    setDocumento(ficha?.documento ?? '')
    setVendedorId(ficha?.vendedorId ?? '')
    setTagIds(ficha?.tags.map((t) => t.id) ?? [])
    setTituloNegocio(negociacao?.titulo ?? '')
    setValorNegocio(negociacao?.valor != null ? String(negociacao.valor).replace('.', ',') : '')
    if (escolheFunil) setFunilEscolhido(funilPadrao ?? funisAtivos[0]?.id ?? '')
    setValoresDeCampo(valoresParaFormulario(ficha?.campos))
  }

  /*
    A recusa da vez anterior não reaparece: o erro fica guardado na mutação, e reabrir a janela
    (de outro contato, inclusive) mostrava "Confira o telefone" de quem já tinha sido corrigido.
  */
  useEffect(() => {
    if (!aberto) return
    criar.reset()
    atualizar.reset()
    atualizarNegociacao.reset()
    excluir.reset()
  }, [aberto])

  const emAndamento = criar.isPending || atualizar.isPending || atualizarNegociacao.isPending
  const erroBruto = criar.error ?? atualizar.error ?? atualizarNegociacao.error ?? excluir.error
  const erro = erroBruto instanceof ErroRequisicao ? erroBruto : null

  const telefoneOculto = Boolean(ficha?.telefoneMascarado)
  /*
    Ciclo fechado não se edita.

    O valor dele é o da venda gravada; deixar mudar aqui faria o card dizer um
    número e o relatório outro, sem nada apontando qual dos dois está certo.
  */
  const cicloFechado = Boolean(negociacao && negociacao.status !== 'aberta')

  function salvar() {
    const dados = {
      nome,
      documento: documento || null,
      campos: montarCampos(campos, valoresDeCampo),
      vendedorId: vendedorId || null,
      tagIds,
      // Só manda telefone quando há algo digitado, para não apagar sem querer
      // o número de quem não tem permissão de ver.
      ...(telefone || !telefoneOculto ? { telefonePrincipal: telefone || null } : {}),
    }

    if (ficha) {
      atualizar.mutate(
        { id: ficha.id, dados },
        {
          onSuccess: () => {
            /*
              Título e valor pertencem ao ciclo, não ao cliente, e por isso vão
              numa chamada própria. Mandar junto voltaria a misturar as duas
              coisas — que é exatamente o que este modelo veio separar.
            */
            if (!negociacao || cicloFechado) return aoFechar()
            atualizarNegociacao.mutate(
              {
                id: negociacao.id,
                dados: { titulo: tituloNegocio || null, valor: lerMoeda(valorNegocio) },
              },
              { onSuccess: aoFechar },
            )
          },
        },
      )
      return
    }

    criar.mutate(
      {
        ...dados,
        ...(escolheFunil && funilAtual
          ? {
              negociacao: {
                funilId: funilAtual.id,
                titulo: tituloNegocio || null,
                valor: lerMoeda(valorNegocio),
              },
            }
          : {}),
      },
      { onSuccess: aoFechar },
    )
  }

  /*
    Excluir contato (ADR 0067): a rota e a permissão `leads.excluir` existiam,
    mas o botão não. O servidor só apaga contato sem histórico (venda, perda,
    atendimento ou mensagem); com histórico, a recusa dele aparece aqui.
  */
  async function apagar() {
    if (!ficha) return
    const ok = await pedirConfirmacao(
      `Excluir ${ficha.nome}? Só dá para excluir contato sem conversa, venda ou perda registrada.`,
      { titulo: 'Excluir contato', rotuloConfirmar: 'Excluir' },
    )
    if (ok) excluir.mutate(ficha.id, { onSuccess: aoFechar })
  }

  return (
    <Modal
      aberto={aberto}
      titulo={
        titulo ??
        (ficha
          ? `Editar ${ficha.nome}`
          : // Enquanto a ficha chega, o nome vem da negociação, e não "Novo lead".
            negociacao
            ? `Editar ${negociacao.contatoNome}`
            : 'Novo lead')
      }
      aoFechar={aoFechar}
      rodape={
        <>
          {ficha && eu && pode(eu, 'leads.excluir') ? (
            <Botao
              variante="texto"
              className={juntar('text-red-600', !aoIniciarNovaNegociacao && 'mr-auto')}
              carregando={excluir.isPending}
              onClick={() => void apagar()}
            >
              Excluir contato
            </Botao>
          ) : null}
          {aoIniciarNovaNegociacao && ficha ? (
            <Botao
              className="mr-auto"
              onClick={() => aoIniciarNovaNegociacao(ficha.id, ficha.nome)}
            >
              Nova negociação
            </Botao>
          ) : null}
          <Botao onClick={aoFechar}>Cancelar</Botao>
          {aba === 'dados' ? (
            <Botao
              variante="primario"
              carregando={emAndamento}
              // Salvar antes de a ficha chegar gravaria o formulário vazio.
              disabled={esperandoFicha}
              onClick={salvar}
            >
              Salvar
            </Botao>
          ) : null}
        </>
      }
    >
      {/*
        Duas abas em vez de uma tela só: o histórico cresce a cada compra, e
        empilhá-lo embaixo do formulário empurraria o botão de salvar para fora
        da vista justamente no cliente mais antigo — o que tem mais o que
        contar. Some no cadastro: quem está sendo criado agora não tem passado.
      */}
      {ficha ? (
        <div className="mb-3 flex gap-1 border-b border-tinta-200">
          {(
            [
              ['dados', 'Dados'],
              [
                'historico',
                `Histórico${ficha.totalNegociacoes > 0 ? ` (${ficha.totalNegociacoes})` : ''}`,
              ],
            ] as const
          ).map(([chave, rotulo]) => (
            <button
              key={chave}
              type="button"
              onClick={() => setAba(chave)}
              className={juntar(
                '-mb-px border-b-2 px-3 py-1.5 text-sm',
                aba === chave
                  ? 'border-marca font-medium text-marca'
                  : 'border-transparent text-tinta-600 hover:text-tinta-900',
              )}
            >
              {rotulo}
            </button>
          ))}
        </div>
      ) : null}

      {aba === 'historico' && ficha ? (
        <div className="space-y-4">
          <HistoricoDeNegociacoes contatoId={ficha.id} destacar={negociacao?.id ?? null} />
          {/*
            O caminho deste ciclo vem depois da lista de ciclos: primeiro
            "quantas vezes este cliente negociou", depois "por onde esta
            negociação andou". A segunda pergunta só existe dentro da primeira.
          */}
          {negociacao ? (
            <section>
              <h3 className="mb-1 text-sm font-medium text-tinta-800">Caminho no funil</h3>
              <MovimentosDaNegociacao negociacaoId={negociacao.id} />
            </section>
          ) : null}
        </div>
      ) : (
        <div className="space-y-3">
          {erro ? <Aviso tipo="erro">{erro.corpo.mensagem}</Aviso> : null}

          {ficha && ficha.duplicados.length > 0 ? (
            <Aviso tipo="atencao">
              Mesmo documento em{' '}
              {plural(ficha.duplicados.length, 'outro registro', 'outros registros')}:{' '}
              {ficha.duplicados.map((d) => d.nome).join(', ')}. Os dois continuam separados de
              propósito (4.9), porque pode ser o segundo número da mesma empresa.
            </Aviso>
          ) : null}

          {/*
          Onde o lead entra. Só no cadastro: contato que já existe muda de
          etapa arrastando o card no quadro, que é onde se vê o funil inteiro.
        */}
          {escolheFunil && !ficha ? (
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-tinta-600">
                Abrir negociação no funil
              </span>
              <select
                value={funilAtual?.id ?? ''}
                onChange={(e) => setFunilEscolhido(e.target.value)}
                className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-2 py-1.5 text-sm"
              >
                {funisAtivos.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <Campo
            rotulo="Nome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            erro={erro?.campo('nome')}
          />

          <Campo
            rotulo="Telefone"
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
            erro={erro?.campo('telefonePrincipal')}
            placeholder={telefoneOculto ? (ficha?.telefonePrincipal ?? '') : '(31) 99999-6935'}
            dica={
              telefoneOculto
                ? 'Seu usuário não tem permissão para ver o número. Deixe em branco para manter o que já está gravado.'
                : 'Com DDD.'
            }
          />

          <Campo
            rotulo="CNPJ ou CPF"
            value={documento}
            onChange={(e) => setDocumento(e.target.value)}
            erro={erro?.campo('documento')}
            dica="O CPF da pessoa. Se o contato for a própria empresa, o CNPJ vai no cadastro dela, em Empresas."
          />

          {/*
            A empresa só aparece em contato que já existe: o vínculo é gravado
            na hora, e contato novo ainda não tem onde gravar. Cadastrou,
            abre de novo e liga.
          */}
          {ficha && eu ? (
            <div>
              <span className="mb-1 block text-sm font-medium text-tinta-800">Empresa</span>
              <SeletorDeEmpresa
                contato={ficha}
                podeEditar={pode(eu, 'leads.editar')}
                podeCriarEmpresa={pode(eu, 'leads.criar')}
              />
              <span className="mt-1 block text-xs text-tinta-400">
                Muda na hora, sem precisar salvar. A empresa manda na carteira: o contato passa para
                o dono dela.
              </span>
            </div>
          ) : null}

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-tinta-800">Vendedor dono</span>
            <select
              value={vendedorId}
              onChange={(e) => setVendedorId(e.target.value)}
              className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-3 py-2 text-sm"
            >
              <option value="">Sem dono (vai para a fila)</option>
              {usuarios
                .filter((u) => u.ativo)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome}
                  </option>
                ))}
            </select>
          </label>

          {/*
          Título e valor são do ciclo, não do cliente.

          Ficam numa caixa à parte por isso: quem edita "Ramon" mexe no
          cliente; quem edita "312.321,00" mexe só nesta compra, e as outras
          que ele já fez continuam valendo o que valeram.
        */}
          {negociacao || (escolheFunil && !ficha) ? (
            <div className="rounded-md ring-1 ring-tinta-200 bg-tinta-50 p-3">
              <p className="mb-2 text-xs font-medium uppercase text-tinta-400">
                {negociacao ? `Negociação em ${negociacao.etapaNome}` : 'Primeira negociação'}
              </p>

              {cicloFechado && negociacao ? (
                <div className="space-y-1 text-sm">
                  <p className="text-tinta-700">
                    {negociacao.titulo ? `${negociacao.titulo}: ` : ''}
                    <strong>{formatarMoeda(negociacao.valor)}</strong>
                  </p>
                  <p className="text-xs text-tinta-400">
                    Ciclo encerrado e por isso não editável: o valor é o da venda registrada. O que
                    foi vendido está na aba Histórico. Para negociar de novo com este cliente, use
                    “Nova negociação”.
                  </p>
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Campo
                    rotulo="Título"
                    value={tituloNegocio}
                    onChange={(e) => setTituloNegocio(e.target.value)}
                    placeholder="Matrícula 2027"
                    dica="Diferencia dois negócios do mesmo cliente."
                  />
                  <Campo
                    rotulo="Valor estimado"
                    value={valorNegocio}
                    onChange={(e) => setValorNegocio(e.target.value)}
                    placeholder="1500,00"
                    // Mudar o valor de negociação existente tem permissão própria (ADR 0038).
                    disabled={Boolean(negociacao) && !pode(eu, 'leads.valor')}
                    dica={
                      negociacao && !pode(eu, 'leads.valor')
                        ? 'Mudar o valor depende de permissão.'
                        : 'Vira o valor da venda quando o card entrar em ganho.'
                    }
                  />
                </div>
              )}
            </div>
          ) : null}

          {tags.length > 0 || pode(eu, 'configuracao.etiquetas') ? (
            <div>
              <span className="mb-1 block text-sm font-medium text-tinta-800">Etiquetas</span>
              <div className="flex flex-wrap items-center gap-2">
                {tags.map((tag) => {
                  const marcada = tagIds.includes(tag.id)
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() =>
                        setTagIds((atual) =>
                          marcada ? atual.filter((t) => t !== tag.id) : [...atual, tag.id],
                        )
                      }
                      className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs"
                      style={{
                        borderColor: tag.cor,
                        backgroundColor: marcada ? tag.cor : 'transparent',
                        color: marcada ? corDoTextoSobre(tag.cor) : undefined,
                      }}
                    >
                      {tag.nome}
                    </button>
                  )
                })}
                {/* Criar na hora, já marcada (ADR 0069); salva junto com a ficha. */}
                {pode(eu, 'configuracao.etiquetas') ? (
                  <BotaoNovaEtiqueta
                    etiquetas={tags}
                    aoCriar={(nova) =>
                      setTagIds((atual) => (atual.includes(nova.id) ? atual : [...atual, nova.id]))
                    }
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          {campos.filter((c) => c.ativo).length > 0 ? (
            <div className="space-y-3 border-t border-tinta-100 pt-3">
              {campos
                .filter((c) => c.ativo)
                .map((campo) => (
                  <CampoCustomizado
                    key={campo.id}
                    campo={campo}
                    valor={valoresDeCampo[campo.chave] ?? ''}
                    erro={erro?.campo(`campos.${campo.chave}`)}
                    aoMudar={(v) => setValoresDeCampo((atual) => ({ ...atual, [campo.chave]: v }))}
                  />
                ))}
            </div>
          ) : null}
        </div>
      )}
      {dialogo}
    </Modal>
  )
}

/**
 * Por onde este card andou.
 *
 * O quadro guardava só a etapa atual, então "por que isto voltou para trás?"
 * não tinha resposta. Com oito pessoas movendo os mesmos cards, a pergunta
 * aparece — e a resposta precisa dizer quem e quando, não só que mudou.
 *
 * Só carrega quando a aba abre: o quadro traz duzentos cards e ninguém abre
 * duzentos históricos.
 */
function MovimentosDaNegociacao({ negociacaoId }: { negociacaoId: string }) {
  const { data: movimentos, isLoading } = useMovimentos(negociacaoId)

  if (isLoading) return <Carregando texto="Buscando o caminho…" />
  if (!movimentos || movimentos.length === 0) {
    return (
      <p className="py-3 text-xs text-tinta-400">
        Nenhuma movimentação registrada. O histórico começa a partir da primeira vez que este card
        muda de etapa.
      </p>
    )
  }

  return (
    <ol className="space-y-1.5 border-t border-tinta-100 pt-3">
      {movimentos.map((m) => (
        <li key={m.id} className="flex items-baseline justify-between gap-3 text-xs">
          <span className="min-w-0 text-tinta-700">
            {m.deEtapaNome ? (
              <>
                <span className="text-tinta-400">{m.deEtapaNome}</span>
                <span className="mx-1 text-tinta-300">&rarr;</span>
              </>
            ) : (
              <span className="mr-1 text-tinta-400">entrou em</span>
            )}
            <span className="font-medium">{m.paraEtapaNome}</span>
            {/* Sem nome é movimento do sistema, não de uma pessoa. */}
            <span className="ml-1 text-tinta-400">por {m.usuarioNome ?? 'sistema'}</span>
          </span>
          <span className="shrink-0 tabular-nums text-tinta-400">
            {formatarDataHora(m.criadoEm)}
          </span>
        </li>
      ))}
    </ol>
  )
}
